import { useEffect, useRef } from 'react';
import * as Tone from 'tone';
import { useStudioStore } from '../store/useStudioStore';

/**
 * Hook to apply tempo automation to Tone.js transport during playback
 * 
 * This hook:
 * - Monitors tempo points and playback state
 * - Updates Tone.js transport BPM based on tempo automation curve
 * - Interpolates between tempo points for smooth transitions
 * - Adjusts time-based effects accordingly
 */
export const useTempoAutomation = () => {
  const { isPlaying, tempoPoints, bpm, getTempoAtTime } = useStudioStore();
  const animationFrameRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);

  useEffect(() => {
    // If not playing or no tempo points, set static BPM
    if (!isPlaying || tempoPoints.length === 0) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      Tone.getTransport().bpm.value = bpm;
      return;
    }

    // Apply tempo automation during playback
    const updateTempo = () => {
      const currentTime = Tone.getTransport().seconds;
      const currentBeat = Tone.getTransport().position as string;
      
      // Parse beat position (format: "bars:quarters:sixteenths")
      const [bars, quarters, sixteenths] = currentBeat.split(':').map(Number);
      const beatsPerBar = Tone.getTransport().timeSignature as number;
      const totalBeats = bars * beatsPerBar + quarters + sixteenths / 4;

      // Get tempo at current time
      const targetBpm = getTempoAtTime(totalBeats);

      // Only update if BPM changed significantly (avoid micro-adjustments)
      const currentBpm = Tone.getTransport().bpm.value;
      if (Math.abs(targetBpm - currentBpm) > 0.1) {
        // Smooth transition to avoid clicks
        Tone.getTransport().bpm.rampTo(targetBpm, 0.05);
      }

      // Continue animation loop
      animationFrameRef.current = requestAnimationFrame(updateTempo);
    };

    // Start tempo automation loop
    updateTempo();

    // Cleanup on unmount or when playback stops
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isPlaying, tempoPoints, bpm, getTempoAtTime]);

  // Update static BPM when not playing
  useEffect(() => {
    if (!isPlaying && tempoPoints.length === 0) {
      Tone.getTransport().bpm.value = bpm;
    }
  }, [bpm, isPlaying, tempoPoints.length]);
};
